/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

// components
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Form } from '@/components/ui/form';
import { DonationFields } from './donation-field';

// hooks
import { useToast } from '@/components/toast';
import { useDonationActions } from '@/hooks/use-donation-actions';

// types
import { createDonationFormSchema, type DonationFormData } from './index';
import { type DonationWidgetProps } from './types';

// value for the donation accordion that will be used for open/close state
const DONATION_FORM_VAL = 'donation';

/**
 * DonationWidget — collapsible amount field + explicit confirm button that
 * adds, replaces, or removes (amount 0/empty) the basket's donation line
 * item. Lives in the order summary next to `PromoCodeForm` and follows the
 * same accordion pattern.
 *
 * The field only submits on explicit confirmation (form submit), never on
 * keystroke, and hydrates pre-filled with `currentAmount` in edit mode
 * (title switches "Make a donation" -> "Edit your donation").
 *
 * @example
 * ```tsx
 * <DonationWidget basketId={basket.basketId} currentAmount={getDonationAmount(basket, donationProductID)} />
 * ```
 */
export const DonationWidget = ({ basketId, currentAmount }: DonationWidgetProps) => {
    const { t } = useTranslation('cart');
    const isEditMode = currentAmount > 0;
    const [isOpen, setIsOpen] = useState(true);
    const { saveDonation, fetcher } = useDonationActions(basketId);
    const { addToast } = useToast();

    const schema = useMemo(() => createDonationFormSchema(t), [t]);

    const form = useForm<DonationFormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            amount: isEditMode ? String(currentAmount) : '',
        },
    });

    // Keep the field in sync with the server-confirmed amount (e.g. after a
    // successful mutation revalidates the basket, or on a fresh page load).
    useEffect(() => {
        form.reset({ amount: currentAmount > 0 ? String(currentAmount) : '' });
        // form is stable and does not need to be in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAmount]);

    useEffect(() => {
        if (fetcher.data) {
            if (fetcher.data.success) {
                addToast(t('donation.successMessage'), 'success');
            } else {
                const errorMessage = t('donation.errorMessage');
                form.setError('amount', { type: 'manual', message: errorMessage });
                addToast(errorMessage, 'error');
            }
        }
        // addToast is stable and does not need to be in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher.data, form, t]);

    const handleSubmit = form.handleSubmit((data) => {
        if (!basketId) {
            form.setError('amount', { type: 'manual', message: t('donation.noBasketMessage') });
            return;
        }

        const amount = data.amount.trim() === '' ? 0 : Number(data.amount);
        saveDonation(amount);
    });

    return (
        <div className="flex w-full flex-col gap-2 pt-2 pb-4">
            <Accordion
                type="single"
                collapsible
                value={isOpen ? DONATION_FORM_VAL : ''}
                onValueChange={(value) => setIsOpen(value === DONATION_FORM_VAL)}
                className="mb-0">
                <AccordionItem value={DONATION_FORM_VAL}>
                    <AccordionTrigger className="justify-start gap-2 pt-1 pb-0 [&>svg]:text-primary">
                        <span className="text-left text-sm font-medium leading-5 text-card-foreground">
                            {isEditMode ? t('donation.editTitle') : t('donation.title')}
                        </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-0 pt-2 pb-2">
                        <Form {...form}>
                            <form onSubmit={(e) => void handleSubmit(e)} data-testid="donation-form">
                                <DonationFields form={form} fetcher={fetcher} isEditMode={isEditMode} />
                            </form>
                        </Form>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
};
